// aluno/materiais.js — tela de materiais do curso (grade completa do MBO).
//
// Origem (monólito): linhas 6373-6661 (HTML materiaisOverlay),
//   5275-5278 (showMateriais — apenas abria o overlay com class 'open').
//
// Nota v19: O que era um overlay modal vira uma rota completa (#/aluno/materiais).
//   A estrutura visual (grupos por professor, links para Kiwify) é preservada
//   fielmente — apenas o mecanismo de exibição mudou (rota em vez de overlay).
//
// Dependências:
//   - router (core/router.js) — router.go('/aluno') para voltar
//
// Exporta:
//   function render(container)

import { router } from '../core/router.js';

// ── Dados dos materiais (extraídos do HTML do monólito) ──────────────────────

// URL base da plataforma Kiwify (mesma em todos os links)
const KIWIFY_URL = 'https://dashboard.kiwify.com/members-area/15a0267c-2464-468a-942f-3b26d6b876da?from=products%2Fedit%2F6624a1f0-f99a-11ed-a6c5-25a9361431e9&tab=content';

/**
 * Lista de professores e suas matérias — preserva 100% do conteúdo original.
 * Cada entrada: { iniciais, nome, materias:[{ icone, titulo }] }
 */
const PROFESSORES = [
  {
    iniciais: 'AN', nome: 'Aline Nepomuceno',
    materias: [
      { icone: '🎭', titulo: 'Técnicas Teatrais Aplicadas à Oratória' },
      { icone: '🎙️', titulo: 'Práticas de Entonação e Declamação' },
      { icone: '🎲', titulo: 'Técnicas de Improviso' },
    ],
  },
  {
    iniciais: 'BF', nome: 'Bruno Freitas',
    materias: [
      { icone: '🧠', titulo: 'PNL aplicada à Oratória' },
      { icone: '🤝', titulo: 'Segredos da Linguagem Corporal' },
    ],
  },
  {
    iniciais: 'BP', nome: 'Bruno Pitanga',
    materias: [
      { icone: '🔬', titulo: 'Neurociência e Autoconhecimento' },
    ],
  },
  {
    iniciais: 'CB', nome: 'Camila Barros',
    materias: [
      { icone: '🗣️', titulo: 'Fonoaudiologia aplicada à Oratória' },
    ],
  },
  {
    iniciais: 'DA', nome: 'Daniella Amaral',
    materias: [
      { icone: '⚡', titulo: 'Mitologia para Oradores' },
    ],
  },
  {
    iniciais: 'DC', nome: 'Dayane Cavalheiro',
    materias: [
      { icone: '✨', titulo: 'Marca Pessoal e Comunicação Visual' },
    ],
  },
  {
    iniciais: 'DW', nome: 'Diego Wildberger',
    materias: [
      { icone: '🎭', titulo: 'Arquétipos e Comunicação' },
    ],
  },
  {
    iniciais: 'DD', nome: 'Diogo Dantas',
    materias: [
      { icone: '✝️', titulo: 'Jesus: Orador e Líder' },
    ],
  },
  {
    iniciais: 'JB', nome: 'Jefferson Beltrão',
    materias: [
      { icone: '📺', titulo: 'Media Training' },
    ],
  },
  {
    iniciais: 'LC', nome: 'Luciano Conceição',
    materias: [
      { icone: '🕊️', titulo: 'Comunicação Não-violenta' },
      { icone: '💬', titulo: 'Comunicação Persuasiva' },
    ],
  },
  {
    iniciais: 'MS', nome: 'Mirleide Sampaio',
    materias: [
      { icone: '🏢', titulo: 'Comunicação Corporativa' },
    ],
  },
  {
    iniciais: 'SP', nome: 'Sandro Pinto',
    materias: [
      { icone: '🤝', titulo: 'Negociação' },
    ],
  },
  {
    iniciais: 'SF', nome: 'Silvana Freire',
    materias: [
      { icone: '🎬', titulo: 'Oratória para Gravação de Vídeos' },
    ],
  },
  {
    iniciais: 'UV', nome: 'Ulla Von Czékus',
    materias: [
      { icone: '🔍', titulo: 'Autoconhecimento para Oratória' },
      { icone: '⏱️', titulo: 'Gestão do Tempo para Oradores' },
      { icone: '🎮', titulo: 'Jogos Empresariais' },
    ],
  },
  {
    iniciais: 'VN', nome: 'Vitor Novaes',
    materias: [
      { icone: '💼', titulo: 'Gestão e Oratória nos Negócios' },
    ],
  },
  {
    iniciais: 'YM', nome: 'Yang Mendes',
    materias: [
      { icone: '📖', titulo: 'Storytelling' },
      { icone: '🚀', titulo: 'Comunicação de Alto Impacto' },
      { icone: '🎯', titulo: 'Práticas Avançadas de Oratória' },
    ],
  },
];

// ── Geradores de HTML ─────────────────────────────────────────────────────────

/**
 * Gera o HTML de um item de matéria.
 * @param {{ icone:string, titulo:string }} materia
 * @returns {string}
 */
function _htmlMateria(materia) {
  return `
    <div class="mat-aula">
      <div class="mat-aula-date"><div class="mat-aula-day">${materia.icone}</div></div>
      <div class="mat-aula-divider"></div>
      <div class="mat-aula-info">
        <div class="mat-aula-title">${materia.titulo}</div>
      </div>
      <a href="${KIWIFY_URL}" target="_blank" rel="noopener"
        class="mat-aula-link" aria-label="Acessar ${materia.titulo} na Kiwify">›</a>
    </div>`;
}

/**
 * Gera o HTML de um grupo de professor.
 * @param {{ iniciais:string, nome:string, materias:Array }} prof
 * @returns {string}
 */
function _htmlProfessor(prof) {
  return `
    <div class="mat-grupo">
      <div class="mat-grupo-titulo">
        <div class="mat-avatar">${prof.iniciais}</div>
        ${prof.nome}
      </div>
      ${prof.materias.map(_htmlMateria).join('')}
    </div>`;
}

// ── Função principal exportada ────────────────────────────────────────────────

/**
 * Renderiza a tela de materiais do curso dentro de `container`.
 * Chamado pelo router para a rota #/aluno/materiais.
 *
 * @param {HTMLElement} container - elemento #app
 */
export function render(container) {
  // Conta total de matérias
  const totalMaterias = PROFESSORES.reduce((acc, p) => acc + p.materias.length, 0);

  container.innerHTML = `
    <div id="mat-root">

      <!-- Header estilo "detail-hero" (mantém visual do overlay original) -->
      <div class="mat-hero">
        <div class="mat-hero-inner">
          <button id="mat-back-btn" class="mat-back-btn" type="button" aria-label="Voltar">←</button>
          <div style="font-size:36px;margin-bottom:8px">📝</div>
          <div class="mat-hero-titulo">Materiais de Aula</div>
          <div class="mat-hero-sub">Grade completa do MBO · ${totalMaterias} matérias</div>
          <a href="${KIWIFY_URL}" target="_blank" rel="noopener" class="mat-kiwify-btn">
            <span style="font-size:16px">▶</span> Acessar Plataforma Kiwify
          </a>
        </div>
      </div>

      <!-- Corpo: lista de professores e matérias -->
      <div class="mat-body" id="mat-body">
        ${PROFESSORES.map(_htmlProfessor).join('')}
      </div>

    </div>
  `;

  // ── Estilos scoped (idempotente) ─────────────────────────────────────────
  if (!document.getElementById('mat-styles')) {
    const s = document.createElement('style');
    s.id = 'mat-styles';
    s.textContent = `
      #mat-root{min-height:100vh;min-height:100dvh;background:#F5F5F5;font-family:'Montserrat',sans-serif}
      .mat-hero{background:linear-gradient(160deg,#6A001F 0%,#B3002D 100%);padding:calc(env(safe-area-inset-top,0px) + 28px) 20px 28px;position:relative;text-align:center}
      .mat-hero-inner{max-width:480px;margin:0 auto}
      .mat-back-btn{position:absolute;top:calc(env(safe-area-inset-top,0px) + 16px);left:16px;width:36px;height:36px;border-radius:50%;border:none;background:rgba(255,255,255,0.2);color:#fff;font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background 0.2s}
      .mat-back-btn:hover{background:rgba(255,255,255,0.32)}
      .mat-hero-titulo{font-size:22px;font-weight:900;color:#fff;margin-bottom:4px}
      .mat-hero-sub{font-size:13px;font-weight:600;color:rgba(255,255,255,0.75);margin-bottom:16px}
      .mat-kiwify-btn{display:inline-flex;align-items:center;gap:8px;padding:12px 24px;background:#fff;color:#B3002D;font-family:'Montserrat',sans-serif;font-weight:700;font-size:14px;border-radius:50px;text-decoration:none;box-shadow:0 4px 16px rgba(0,0,0,0.15)}
      .mat-body{padding:16px 20px 80px}
      .mat-grupo{background:#fff;border-radius:16px;margin-bottom:16px;overflow:hidden;box-shadow:0 2px 10px rgba(0,0,0,0.06)}
      .mat-grupo-titulo{display:flex;align-items:center;gap:10px;padding:14px 16px;font-size:14px;font-weight:800;color:#0A0A0A;border-bottom:1px solid #F2F2F7}
      .mat-avatar{width:32px;height:32px;border-radius:50%;background:linear-gradient(135deg,#6A001F,#B3002D);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;color:#fff;flex-shrink:0}
      .mat-aula{display:flex;align-items:center;gap:12px;padding:12px 16px;border-bottom:1px solid #F2F2F7}
      .mat-aula:last-child{border-bottom:none}
      .mat-aula-date{display:flex;align-items:center;justify-content:center;width:32px;flex-shrink:0}
      .mat-aula-day{font-size:20px}
      .mat-aula-divider{width:1px;height:24px;background:#E8E8E8;flex-shrink:0}
      .mat-aula-info{flex:1;min-width:0}
      .mat-aula-title{font-size:13px;font-weight:700;color:#0A0A0A;line-height:1.3}
      .mat-aula-link{color:#B3002D;font-size:20px;text-decoration:none;display:flex;align-items:center;flex-shrink:0}
      .mat-aula-link:hover{color:#6A001F}
    `;
    document.head.appendChild(s);
  }

  // ── Wiring de eventos ─────────────────────────────────────────────────────

  container.querySelector('#mat-back-btn').addEventListener('click', () => {
    router.go('/aluno');
  });
}
